import type { SpecRawValue } from '@inference.engine/ad-formats/web';
import { z } from 'zod';
import { MESSAGE_KEYS, type MessageCode } from '../lib/message-keys';
import type {
  AuditEvent,
  FormatLibrary,
  ImportPreview,
  LibraryState,
  RowOutcome,
  SaveResult,
} from '../model/types';

/**
 * Parsed on arrival from the admin API. Each schema is annotated with the type the panel uses, so
 * a field added to one side and not the other fails to compile rather than arriving unchecked.
 */

const status = z.enum(['active', 'deprecated']);
const ownerType = z.enum(['profile', 'format']);
const nullableText = z.string().nullable();
const provenance = {
  requirement: z.string(),
  scope: z.string(),
  notes: nullableText,
};
const messageCode = z.enum(Object.keys(MESSAGE_KEYS) as [MessageCode, ...MessageCode[]]);
const coded = {
  code: messageCode,
  params: z.record(z.string(), z.union([z.string(), z.number()])),
  message: z.string(),
};

const libraryGroup = z.object({
  key: z.string(),
  name: z.string(),
  sortOrder: z.number(),
  status,
  revision: z.number(),
});

const libraryFormat = z.object({
  key: z.string(),
  groupKey: z.string(),
  surface: nullableText,
  name: z.string(),
  aliases: z.array(z.string()),
  w: z.number(),
  h: z.number(),
  specProfileKey: nullableText,
  status,
  sortOrder: z.number(),
  notes: nullableText,
  revision: z.number(),
});

const libraryProfile = z.object({
  key: z.string(),
  name: z.string(),
  groupKey: nullableText,
  surface: nullableText,
  revision: z.number(),
});

const librarySpecValue = z.object({
  ownerType,
  ownerKey: z.string(),
  mediaType: z.string(),
  property: z.string(),
  // The raw value is checked by the save that wrote it, so the panel takes it as sent.
  value: z.custom<SpecRawValue | null>(),
  ...provenance,
});

const libraryTextLimit = z.object({
  ownerType,
  ownerKey: z.string(),
  field: z.string(),
  maxChars: z.number().nullable(),
  recommendedChars: z.number().nullable(),
  truncatesAt: z.number().nullable(),
  allowedValues: z.array(z.string()).nullable(),
  ...provenance,
});

const libraryState = z.object({
  groups: z.array(libraryGroup),
  formats: z.array(libraryFormat),
  profiles: z.array(libraryProfile),
  specValues: z.array(librarySpecValue),
  textLimits: z.array(libraryTextLimit),
}) satisfies z.ZodType<LibraryState>;

export const formatLibrarySchema = z.object({
  datasetVersion: z.number(),
  state: libraryState,
}) satisfies z.ZodType<FormatLibrary>;

export const saveResultSchema = z.object({
  datasetVersion: z.number(),
  changedRecords: z.number(),
}) satisfies z.ZodType<SaveResult>;

export const auditEventPageSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      datasetVersion: z.number(),
      origin: z.enum(['edit', 'import', 'restore']),
      entityType: z.string(),
      entityKey: z.string(),
      createdAt: z.string(),
      before: z.record(z.string(), z.unknown()).nullable(),
      after: z.record(z.string(), z.unknown()).nullable(),
    }) satisfies z.ZodType<AuditEvent>,
  ),
});

const outcomes = z.enum([
  'new',
  'updated',
  'unchanged',
  'conflict',
  'ignored',
]) satisfies z.ZodType<RowOutcome>;

export const importPreviewSchema = z.object({
  id: z.string(),
  baseVersion: z.number(),
  warnings: z.array(z.object(coded)),
  counts: z.record(z.string(), z.record(outcomes, z.number())),
  ignored: z.array(
    z.object({ ...coded, file: z.string(), row: z.number(), column: z.string().optional() }),
  ),
  ignoredCount: z.number(),
  ignoredTruncated: z.boolean(),
  conflicts: z.array(
    z.object({
      id: z.string(),
      file: z.string(),
      row: z.number(),
      fileValue: z.unknown(),
      currentValue: z.unknown(),
    }),
  ),
  resolutions: z.record(z.string(), z.enum(['file', 'current'])),
  changes: z.array(
    z.object({ entityType: z.string(), entityKey: z.string(), operation: z.string() }),
  ),
  breakingFormats: z.array(z.string()),
  confirmBreaking: z.boolean(),
  issues: z.array(
    z.object({
      ...coded,
      entityType: z.string(),
      entityKey: z.string(),
      field: z.string().optional(),
    }),
  ),
  canConfirm: z.boolean(),
}) satisfies z.ZodType<ImportPreview>;
